/**
 * TuneupsDueReport (AGR-NEXT-1) — the "nobody forgets a tune-up" worklist. Every
 * membership tune-up Pegasus auto-created when an agreement was sold shows up
 * here as its season nears; one click drops it into the schedule queue as a real
 * ticket. The continuous-membership engine made visible. Read from
 * agreement_visit (source='generated'); scheduling materializes a pending
 * pegasus_dispatch and links it back.
 */
import { useMemo, useState } from "react";
import { format, parseISO, differenceInCalendarDays } from "date-fns";
import { Flame, Snowflake, CalendarPlus, X, CheckCircle2, ChevronDown, CalendarClock, RefreshCw } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { InfoTip } from "@/components/InfoTip";
import { CustomerLink } from "@/components/links";
import { cn } from "@/lib/cn";
import { useTuneupsDue, useScheduleTuneup, useSkipTuneup, type TuneupDueRow } from "@/features/agreements/obligations";

const WINDOWS: [number, string][] = [[60, "Next 60 days"], [90, "Next 90 days"], [180, "Next 6 months"], [100000, "All upcoming"]];

const fmt = (d: string) => { try { return format(parseISO(d), "MMM d, yyyy"); } catch { return d; } };
const CatIcon = ({ cat }: { cat: string }) => cat === "cooling"
  ? <Snowflake className="h-4 w-4 text-sky-500" />
  : <Flame className="h-4 w-4 text-orange-500" />;

export function TuneupsDueReport() {
  const { data, isLoading } = useTuneupsDue();
  const schedule = useScheduleTuneup();
  const skip = useSkipTuneup();
  const [windowDays, setWindowDays] = useState(90);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showScheduled, setShowScheduled] = useState(false);

  const rows = data ?? [];
  const open = useMemo(() => rows.filter((r) => r.status === "open"), [rows]);
  const scheduled = useMemo(() => rows.filter((r) => r.status === "scheduled"), [rows]);
  const today = new Date();

  const due = useMemo(
    () => open.filter((r) => differenceInCalendarDays(parseISO(r.target_date), today) <= windowDays),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, windowDays],
  );
  const laterCount = open.length - due.length;
  const renewalDue = useMemo(() => due.filter((r) => r.is_renewal_check).length, [due]);

  const doSchedule = (r: TuneupDueRow) => { setBusyId(r.id); schedule.mutate(r, { onSettled: () => setBusyId(null) }); };
  const doSkip = (r: TuneupDueRow) => { if (window.confirm(`Mark "${r.task_label}" for ${r.customer_name} as not needed?`)) skip.mutate(r); };

  return (
    <div className="min-w-0 flex-1">
      {/* Header */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600"><CalendarClock className="h-4 w-4" /></span>
          <div className="flex items-center gap-1.5 text-sm font-bold text-slate-800">
            Tune-ups Due
            <InfoTip label="About tune-ups due">Tune-ups created automatically when a membership plan is sold. Book them before the season starts; Schedule adds one to the schedule queue.</InfoTip>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs tabular-nums text-slate-500"><strong className="text-slate-800">{due.length}</strong> ready to schedule</span>
          {renewalDue > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700">
              <RefreshCw className="h-3 w-3" /> {renewalDue} renewal{renewalDue === 1 ? "" : "s"} due
            </span>
          )}
          <select value={windowDays} onChange={(e) => setWindowDays(Number(e.target.value))} className="rounded-md border border-hairline bg-white px-2 py-1.5 text-xs font-medium text-slate-700 focus:border-brand-400 focus:outline-none">
            {WINDOWS.map(([d, l]) => <option key={d} value={d}>{l}</option>)}
          </select>
        </div>
      </div>

      {isLoading ? (
        <Card className="h-40 animate-pulse bg-slate-100" />
      ) : due.length === 0 ? (
        <Card className="p-10 text-center">
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-300" />
          <h3 className="mt-3 text-[15px] font-bold tracking-tight text-slate-900">Nothing due in this window</h3>
          <p className="mt-1 text-xs text-slate-500">
            {open.length > 0 ? `${open.length} membership tune-up${open.length === 1 ? "" : "s"} further out — widen the window to see them.` : "Sell a PLUS agreement and its tune-ups will appear here automatically."}
          </p>
        </Card>
      ) : (
        <Card className="divide-y divide-slate-100 overflow-hidden">
          {due.map((r) => {
            const days = differenceInCalendarDays(parseISO(r.target_date), today);
            const overdue = days < 0;
            return (
              <div key={r.id} className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-slate-50/60">
                <CatIcon cat={r.covers_category} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <CustomerLink custno={r.custno} locno={r.locno ?? undefined} className="truncate text-sm font-medium">{r.customer_name}</CustomerLink>
                    <span className="shrink-0 text-xs text-slate-500">· {r.task_label}</span>
                    {r.is_renewal_check && (
                      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">
                        <RefreshCw className="h-3 w-3" /> Renewal
                      </span>
                    )}
                  </div>
                  <div className={cn("text-[11px]", r.is_renewal_check ? "font-medium text-amber-600" : "text-slate-400")}>
                    {r.sequence_label}{r.is_renewal_check ? " · last check of the term — offer renewal at this visit" : ""}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className={cn("text-xs font-medium tabular-nums", overdue ? "text-red-600" : "text-slate-600")}>{fmt(r.target_date)}</div>
                  <div className={cn("text-[11px]", overdue ? "text-red-500" : "text-slate-400")}>{overdue ? `${Math.abs(days)}d overdue` : days === 0 ? "today" : `in ${days}d`}</div>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <button
                    onClick={() => doSchedule(r)}
                    disabled={busyId === r.id}
                    className="inline-flex items-center gap-1 rounded-md bg-brand-600 px-2.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-700 disabled:bg-brand-300"
                  >
                    <CalendarPlus className="h-3.5 w-3.5" /> {busyId === r.id ? "Scheduling…" : "Schedule"}
                  </button>
                  <button onClick={() => doSkip(r)} title="Not needed" className="rounded-md p-1.5 text-slate-300 transition-colors hover:bg-slate-100 hover:text-slate-600"><X className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            );
          })}
        </Card>
      )}

      {laterCount > 0 && due.length > 0 && (
        <p className="mt-2 text-[11px] text-slate-400">+ {laterCount} more tune-up{laterCount === 1 ? "" : "s"} further out — widen the window to bring them forward.</p>
      )}

      {/* Already in the queue */}
      {scheduled.length > 0 && (
        <div className="mt-4">
          <button onClick={() => setShowScheduled((s) => !s)} className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700">
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", showScheduled && "rotate-180")} />
            {scheduled.length} already in the schedule queue
          </button>
          {showScheduled && (
            <Card className="mt-2 divide-y divide-slate-100 overflow-hidden">
              {scheduled.map((r) => (
                <div key={r.id} className="flex items-center gap-3 px-4 py-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                  <CustomerLink custno={r.custno} locno={r.locno ?? undefined} className="truncate font-medium">{r.customer_name}</CustomerLink>
                  <span className="text-xs text-slate-500">· {r.task_label}</span>
                  <span className="ml-auto text-[11px] text-slate-400">in the queue · {fmt(r.target_date)}</span>
                </div>
              ))}
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
